import { Alert, Box, Stack, TextField, Typography } from "@mui/material"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { requestPasswordReset } from "../../../data-utils/data"
import { ROUTE_PATHS } from "../../../utilities/constants.js"
import { UiCtButton } from "../../common/MuiStyled/LibComponent.jsx"
import { LoginPanelShell } from "./SetPassword"
import "./Login.scss"

/**
 * "Forgot password?" — reached from the Keycloak sign-in page, which pre-fills `?email=`.
 *
 * The server emails a one-time link to the existing set-password page, and only for an
 * account that signs in with a password; email-code and SSO accounts get nothing. It
 * answers identically for every address, so this page shows the same confirmation
 * whatever was typed — it must not hint whether an account exists.
 */
export const ForgotPassword = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get("email") ?? "")
  // `?setup=1`: sent here by the sign-in page because the account has no password yet
  // (e.g. its organization just switched single sign-on off).
  const setup = searchParams.get("setup") === "1"
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    const result = await requestPasswordReset({ email: email.trim() })
    setBusy(false)
    if (!result.ok) return setError(result.message)
    setSent(true)
  }

  if (sent) {
    return (
      <LoginPanelShell>
        <Stack sx={{ gap: 2, maxWidth: 340, textAlign: "center" }}>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600, color: "text.primary" }}>
            Check your email
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
            If <strong>{email.trim()}</strong> has a ChaosTrack account that signs in with a password, we&rsquo;ve sent it a link to choose a new
            one. The link works once and expires in an hour.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13 }}>
            Sign in with an emailed code or your company&rsquo;s single sign-on? You don&rsquo;t have a ChaosTrack password &mdash; go back and
            sign in the usual way.
          </Typography>
          <UiCtButton variant="contained" onClick={() => navigate(ROUTE_PATHS.ROOT)} sx={{ py: 1 }}>
            Back to sign in
          </UiCtButton>
        </Stack>
      </LoginPanelShell>
    )
  }

  return (
    <LoginPanelShell>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: 340 }}>
        <Stack sx={{ gap: 2, alignItems: "stretch" }}>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600, color: "text.primary", textAlign: "center" }}>
            {setup ? "Set your password" : "Reset your password"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", fontSize: 14 }}>
            {setup
              ? "Your account doesn’t have a password yet. We’ll email you a link to choose one."
              : "Enter your email address and we’ll send you a link to choose a new password."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="email"
            label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputProps={{ autoComplete: "email", maxLength: 320 }}
            disabled={busy}
          />
          {error && (
            <Alert severity="error" sx={{ fontSize: 13, py: 0 }}>
              {error}
            </Alert>
          )}
          <UiCtButton type="submit" variant="contained" disabled={busy || !email.trim()} sx={{ py: 1 }}>
            {busy ? "Sending…" : setup ? "Send me a link" : "Send reset link"}
          </UiCtButton>
          <UiCtButton variant="text" onClick={() => navigate(ROUTE_PATHS.ROOT)} disabled={busy}>
            Back to sign in
          </UiCtButton>
        </Stack>
      </Box>
    </LoginPanelShell>
  )
}

export default ForgotPassword
